
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Zap, Calendar, TrendingUp, AlertCircle, CheckCircle, XCircle } from "lucide-react";
import { ActivityPrice } from "@/hooks/useFinanceData";
import { useMemo } from "react";

interface ActivityPricesCardProps {
  prices: ActivityPrice[];
}

export const ActivityPricesCard = ({ prices }: ActivityPricesCardProps) => {
  // Agrupar precios por actividad
  const pricesByActivity = useMemo(() => {
    const byActivity = new Map<string, {
      activityId: string;
      activityName: string;
      perSessionPrice?: ActivityPrice;
      monthlyPrice?: ActivityPrice;
    }>();

    prices.forEach(price => {
      const activityId = price.activity_id;
      const activityName = price.activity?.name || 'Actividad desconocida';
      
      if (!byActivity.has(activityId)) {
        byActivity.set(activityId, {
          activityId,
          activityName,
        });
      }

      const activity = byActivity.get(activityId)!;
      if (price.billing_type_target === 'per_session') {
        activity.perSessionPrice = price;
      } else if (price.billing_type_target === 'monthly') {
        activity.monthlyPrice = price;
      }
    });

    return Array.from(byActivity.values());
  }, [prices]);

  // Estadísticas de configuración
  const configStats = useMemo(() => {
    const complete = pricesByActivity.filter(a => a.perSessionPrice && a.monthlyPrice).length;
    const partial = pricesByActivity.filter(a => (a.perSessionPrice && !a.monthlyPrice) || (!a.perSessionPrice && a.monthlyPrice)).length;
    const total = pricesByActivity.length;
    
    return { complete, partial, total };
  }, [pricesByActivity]);

  const getConfigurationStatus = (activity: any) => {
    const hasPerSession = !!activity.perSessionPrice;
    const hasMonthly = !!activity.monthlyPrice;
    
    if (hasPerSession && hasMonthly) {
      return { status: 'complete', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-950/20', border: 'border-green-200 dark:border-green-800' };
    } else if (hasPerSession || hasMonthly) {
      return { status: 'partial', icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950/20', border: 'border-amber-200 dark:border-amber-800' };
    } else {
      return { status: 'missing', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/20', border: 'border-red-200 dark:border-red-800' };
    }
  };

  if (prices.length === 0) {
    return (
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Estado de Configuración de Precios
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-4">
            No hay precios configurados para ninguna actividad
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Métricas de configuración */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Estado de Configuración por Actividad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-center gap-1 mb-1">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-900 dark:text-green-100">Completas</span>
              </div>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{configStats.complete}</p>
              <p className="text-xs text-green-600 dark:text-green-400">
                Ambos precios configurados
              </p>
            </div>
            
            <div className="text-center p-3 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-center justify-center gap-1 mb-1">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-900 dark:text-amber-100">Parciales</span>
              </div>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{configStats.partial}</p>
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Solo un tipo de precio
              </p>
            </div>

            <div className="text-center p-3 bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/20 dark:to-rose-950/20 rounded-lg border border-red-200 dark:border-red-800">
              <div className="flex items-center justify-center gap-1 mb-1">
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-red-900 dark:text-red-100">Sin Configurar</span>
              </div>
              <p className="text-2xl font-bold text-red-700 dark:text-red-300">{configStats.total - configStats.complete - configStats.partial}</p>
              <p className="text-xs text-red-600 dark:text-red-400">
                Requieren atención
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de actividades con su estado de configuración */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="-flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Configuración de Precios por Actividad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {pricesByActivity.map((activity) => {
              const config = getConfigurationStatus(activity);
              const StatusIcon = config.icon;
              
              return (
                <div key={activity.activityId} className={`flex items-center justify-between p-4 rounded-lg border ${config.bg} ${config.border}`}>
                  <div className="flex items-center gap-3">
                    <StatusIcon className={`w-5 h-5 ${config.color}`} />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{activity.activityName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {activity.perSessionPrice && (
                          <Badge variant="outline" className="text-xs bg-white dark:bg-gray-800">
                            <Zap className="w-3 h-3 mr-1" />
                            {activity.perSessionPrice.price_amount}€/sesión
                          </Badge>
                        )}
                        {activity.monthlyPrice && (
                          <Badge variant="outline" className="text-xs bg-white dark:bg-gray-800">
                            <Calendar className="w-3 h-3 mr-1" />
                            {activity.monthlyPrice.price_amount}€/mes
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    {config.status === 'complete' && (
                      <p className="text-sm font-medium text-green-700 dark:text-green-300">
                        ✅ Configuración completa
                      </p>
                    )}
                    {config.status === 'partial' && (
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                        ⚠️ Falta {!activity.perSessionPrice ? 'precio por día' : 'cuota mensual'}
                      </p>
                    )}
                    {config.status === 'missing' && (
                      <p className="text-sm font-medium text-red-700 dark:text-red-300">
                        ❌ Sin precios configurados
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-md border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              💡 <strong>Recomendación:</strong> Configura ambos tipos de precio para cada actividad. Los padres podrán elegir entre pago por día o cuota mensual durante la inscripción.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
